import { useEffect, useRef, useState } from 'react';
import { useApi } from '../../../utils/hooks';
import { debounce } from 'lodash';
import { Helpers } from '../../../utils/helpers';

const useForgotPasswordForm = () => {
  const api = useApi();
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const [forgotPasswordResult, setForgotPasswordResult] = useState<boolean | null>(null);
  const [emailValid, setEmailValid] = useState<boolean>(true);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await sendResetEmail();
  };

  const sendResetEmail = async (): Promise<void> => {
    setIsWorking(true);
    setForgotPasswordResult(null);
    const apiResponse = await api.post<boolean>(
      '/auth/forgot-password',
      JSON.stringify(emailAddress)
    );
    if (!apiResponse || !apiResponse.response || apiResponse.status.statusCode !== 200) {
      setForgotPasswordResult(false);
    } else {
      setForgotPasswordResult(apiResponse.response);
    }
    setIsWorking(false);
  };

  const updateEmailAddress = (value: string): void => {
    setEmailAddress(value);
  };

  const debounceEmailValid = useRef(
    debounce((validateEmailAddress: string) => {
      if (validateEmailAddress.trim().length > 0) {
        const valid = Helpers.isValidEmail(validateEmailAddress);
        setEmailValid(valid);
        setSubmitDisabled(!valid);
      } else {
        setSubmitDisabled(true);
      }
    }, 500)
  ).current;

  // waits for debounceEmailValid to resolve its debounce window and then clears the window
  useEffect(() => {
    return () => debounceEmailValid.cancel();
  }, [debounceEmailValid]);

  useEffect(() => {
    setSubmitDisabled(true);
    debounceEmailValid(emailAddress);
  }, [emailAddress, debounceEmailValid]);

  return {
    emailAddress,
    isWorking,
    handleSubmit,
    sendResetEmail,
    updateEmailAddress,
    forgotPasswordResult,
    emailValid,
    submitDisabled
  };
};

export default useForgotPasswordForm;
